import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import {
  Check,
  Circle,
  Copy,
  Download,
  ExternalLink,
  ImageUp,
  Link2,
  Loader2,
  Plus,
  QrCode,
  RotateCcw,
  Search,
  Square,
  SquareRoundCorner,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_STYLE = { fg_color: "#7c3aed", bg_color: "#ffffff", module_style: "square", logo_url: null };
const PRESET_COLORS = [
  { fg: "#7c3aed", bg: "#ffffff", name: "Purple" },
  { fg: "#0f172a", bg: "#ffffff", name: "Classic" },
  { fg: "#2563eb", bg: "#ffffff", name: "Blue" },
  { fg: "#059669", bg: "#ffffff", name: "Green" },
  { fg: "#db2777", bg: "#ffffff", name: "Pink" },
  { fg: "#f8fafc", bg: "#0f172a", name: "Dark" },
];
const MODULE_STYLES = [
  { value: "square", label: "Kotak", icon: Square },
  { value: "rounded", label: "Rounded", icon: SquareRoundCorner },
  { value: "dots", label: "Dots", icon: Circle },
];
const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB, sinkron dengan backend

const shortUrlFor = (link) => `${window.location.origin}/${link.short_code}`;

const QRCodePage = () => {
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  const [search, setSearch] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [creatingLink, setCreatingLink] = useState(false);

  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [size, setSize] = useState([512]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewQr, setPreviewQr] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const logoInputRef = useRef(null);
  const editorRef = useRef(null);

  const selectedLink = useMemo(
    () => links.find((link) => link.link_id === selectedLinkId) || null,
    [links, selectedLinkId]
  );
  const isDirty = selectedLink
    ? JSON.stringify(style) !== JSON.stringify({ ...DEFAULT_STYLE, ...(selectedLink.qr_style || {}) })
    : false;

  const loadLinks = useCallback(async () => {
    setLinksLoading(true);
    try {
      const response = await axios.get(`${API}/links`, { withCredentials: true });
      setLinks(response.data);
      setSelectedLinkId((current) => current || response.data[0]?.link_id || null);
    } catch (error) {
      toast.error("Gagal memuat daftar shortlink");
    } finally {
      setLinksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // Saat pilih link lain, muat gaya QR yang sudah tersimpan (jika ada).
  useEffect(() => {
    if (!selectedLink) return;
    setStyle({ ...DEFAULT_STYLE, ...(selectedLink.qr_style || {}) });
    setPreviewQr(selectedLink.qr_code || null);
  }, [selectedLinkId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live preview: regenerasi pratinjau (tidak tersimpan) tiap gaya berubah.
  useEffect(() => {
    if (!selectedLink) return;
    setIsPreviewing(true);
    const delay = setTimeout(async () => {
      try {
        const response = await axios.post(`${API}/qrcode`, {
          url: shortUrlFor(selectedLink),
          fg_color: style.fg_color,
          bg_color: style.bg_color,
          module_style: style.module_style,
          logo_url: style.logo_url,
          size: size[0],
        });
        setPreviewQr(response.data.qr_code);
      } catch (error) {
        toast.error("Gagal membuat pratinjau QR Code");
      } finally {
        setIsPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [style, size, selectedLinkId]); // eslint-disable-line react-hooks/exhaustive-deps

  const createLinkFromUrl = async () => {
    if (!newUrl.trim()) {
      toast.error("Masukkan URL terlebih dahulu");
      return;
    }
    setCreatingLink(true);
    try {
      const response = await axios.post(
        `${API}/links`,
        { original_url: newUrl.trim() },
        { withCredentials: true }
      );
      setLinks((current) => [response.data, ...current]);
      setSelectedLinkId(response.data.link_id);
      setNewUrl("");
      toast.success("Shortlink dibuat, siap didesain QR-nya");
      requestAnimationFrame(() => editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal membuat shortlink");
    } finally {
      setCreatingLink(false);
    }
  };

  const uploadLogo = async (file) => {
    const looksLikeImage =
      file.type.startsWith("image/") || /\.(heic|heif)$/i.test(file.name);
    if (!looksLikeImage) {
      toast.error("File harus berupa gambar");
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      toast.error("Ukuran logo maksimal 2MB");
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/upload/qr-logo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStyle((current) => ({ ...current, logo_url: response.data.url }));
      toast.success("Logo diunggah");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal mengunggah logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoInput = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) uploadLogo(file);
  };

  const handleLogoDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) uploadLogo(file);
  };

  const saveQrStyle = async () => {
    if (!selectedLink) return;
    setIsSaving(true);
    try {
      const response = await axios.put(
        `${API}/links/${selectedLink.link_id}/qrcode`,
        { ...style, size: size[0] },
        { withCredentials: true }
      );
      setLinks((current) =>
        current.map((link) => (link.link_id === selectedLink.link_id ? response.data : link))
      );
      setPreviewQr(response.data.qr_code);
      toast.success("QR Code tersimpan ke shortlink ini");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan QR Code");
    } finally {
      setIsSaving(false);
    }
  };

  const resetStyle = () => {
    if (!selectedLink) return;
    setStyle({ ...DEFAULT_STYLE, ...(selectedLink.qr_style || {}) });
  };

  const downloadQr = () => {
    if (!previewQr || !selectedLink) return;
    const link = document.createElement("a");
    link.href = previewQr;
    link.download = `qrcode-${selectedLink.short_code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyQr = async () => {
    if (!previewQr) return;
    if (!navigator.clipboard?.write || !window.isSecureContext) {
      toast.error("Salin gambar hanya didukung di HTTPS. Gunakan tombol Download.");
      return;
    }
    try {
      const response = await fetch(previewQr);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      toast.success("QR Code disalin ke clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Gagal menyalin QR Code");
    }
  };

  const filteredLinks = links.filter((link) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return (
      link.short_code.toLowerCase().includes(query) ||
      (link.title || "").toLowerCase().includes(query) ||
      link.original_url.toLowerCase().includes(query)
    );
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qrcode-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
            QR Code Studio
          </h1>
          <p className="text-muted-foreground mt-1">
            Desain QR code untuk shortlink Anda — tersimpan otomatis dan bisa dibuka lagi kapan saja.
          </p>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,340px)_1fr] gap-6">
          {/* Pilih / buat link */}
          <Card className="dashboard-card lg:row-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="w-5 h-5" /> Pilih Shortlink
              </CardTitle>
              <CardDescription>QR code melekat pada shortlink dan tersimpan otomatis.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Tempel URL untuk buat baru..."
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createLinkFromUrl()}
                />
                <Button onClick={createLinkFromUrl} disabled={creatingLink} size="icon" title="Buat shortlink baru">
                  {creatingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cari shortlink Anda..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                {linksLoading && (
                  <p className="text-sm text-muted-foreground text-center py-8">Memuat shortlink...</p>
                )}
                {!linksLoading && filteredLinks.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {links.length === 0 ? "Belum ada shortlink. Buat satu di atas." : "Tidak ditemukan."}
                  </p>
                )}
                {filteredLinks.map((link) => (
                  <button
                    key={link.link_id}
                    type="button"
                    onClick={() => setSelectedLinkId(link.link_id)}
                    className={`w-full flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${
                      link.link_id === selectedLinkId
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                      {link.qr_code ? (
                        <img src={link.qr_code} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <QrCode className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{link.title || `/${link.short_code}`}</p>
                      <p className="text-xs text-muted-foreground truncate">/{link.short_code}</p>
                    </div>
                    {link.qr_style && (
                      <Badge variant="outline" className="text-[10px] shrink-0">Custom</Badge>
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Editor */}
          <Card className="dashboard-card" ref={editorRef}>
            <CardHeader>
              <CardTitle className="text-lg">Desain</CardTitle>
              <CardDescription>
                {selectedLink ? `Mengedit QR untuk /${selectedLink.short_code}` : "Pilih shortlink terlebih dahulu"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!selectedLink ? (
                <p className="text-center text-muted-foreground py-12">
                  Pilih atau buat shortlink di panel kiri untuk mulai mendesain.
                </p>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Preset Warna</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {PRESET_COLORS.map((preset) => (
                        <Button
                          key={preset.name}
                          type="button"
                          variant="outline"
                          className={`h-auto py-2.5 flex flex-col items-center gap-1.5 ${
                            style.fg_color === preset.fg && style.bg_color === preset.bg ? "border-primary" : ""
                          }`}
                          onClick={() => setStyle((c) => ({ ...c, fg_color: preset.fg, bg_color: preset.bg }))}
                        >
                          <div
                            className="w-7 h-7 rounded border grid grid-cols-3 grid-rows-3 gap-0.5 p-1"
                            style={{ backgroundColor: preset.bg, borderColor: preset.fg }}
                          >
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                              <div
                                key={i}
                                className="rounded-sm"
                                style={{ backgroundColor: [0, 2, 4, 6, 8].includes(i) ? preset.fg : "transparent" }}
                              />
                            ))}
                          </div>
                          <span className="text-[11px]">{preset.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Warna Depan</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={style.fg_color}
                          onChange={(e) => setStyle((c) => ({ ...c, fg_color: e.target.value }))}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          value={style.fg_color}
                          onChange={(e) => setStyle((c) => ({ ...c, fg_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Warna Latar</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={style.bg_color}
                          onChange={(e) => setStyle((c) => ({ ...c, bg_color: e.target.value }))}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          value={style.bg_color}
                          onChange={(e) => setStyle((c) => ({ ...c, bg_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bentuk Modul</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {MODULE_STYLES.map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          variant="outline"
                          className={`h-auto py-3 flex flex-col items-center gap-1.5 ${
                            style.module_style === option.value ? "border-primary" : ""
                          }`}
                          onClick={() => setStyle((c) => ({ ...c, module_style: option.value }))}
                        >
                          <option.icon className="w-5 h-5" />
                          <span className="text-[11px]">{option.label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Logo di Tengah (opsional)</Label>
                    <div
                      onDrop={handleLogoDrop}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={() => setIsDragOver(false)}
                      className={`rounded-lg border-2 border-dashed p-4 flex items-center gap-4 transition-colors ${
                        isDragOver ? "border-primary bg-primary/5" : "border-border"
                      }`}
                    >
                      <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                        {uploadingLogo ? (
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        ) : style.logo_url ? (
                          <img src={style.logo_url} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                          <ImageUp className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">Seret &amp; lepas logo di sini, atau</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={uploadingLogo}
                          >
                            Pilih File
                          </Button>
                          {style.logo_url && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => setStyle((c) => ({ ...c, logo_url: null }))}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus
                            </Button>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">PNG/JPG/WebP, maks 2MB.</p>
                      </div>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*,.heic,.heif"
                        className="hidden"
                        onChange={handleLogoInput}
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Ukuran Unduhan</Label>
                      <span className="text-sm text-muted-foreground">{size[0]}px</span>
                    </div>
                    <Slider value={size} onValueChange={setSize} min={256} max={1024} step={64} />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <Button className="flex-1 h-11" onClick={saveQrStyle} disabled={isSaving || !isDirty}>
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      {isSaving ? "Menyimpan..." : isDirty ? "Simpan ke Shortlink Ini" : "Tersimpan"}
                    </Button>
                    {isDirty && (
                      <Button variant="outline" size="icon" onClick={resetStyle} title="Batalkan perubahan">
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Pratinjau</CardTitle>
              <CardDescription>
                {selectedLink ? (
                  <a
                    href={shortUrlFor(selectedLink)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {shortUrlFor(selectedLink)} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  "Belum ada shortlink dipilih"
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center">
                <div
                  className="qr-container rounded-2xl p-8 mb-6 relative"
                  style={{ backgroundColor: style.bg_color || "#ffffff" }}
                >
                  {previewQr ? (
                    <img
                      src={previewQr}
                      alt="QR Code"
                      className={`w-56 h-56 sm:w-64 sm:h-64 transition-opacity ${isPreviewing ? "opacity-50" : ""}`}
                    />
                  ) : (
                    <div className="w-56 h-56 sm:w-64 sm:h-64 flex items-center justify-center border-2 border-dashed border-muted rounded-xl">
                      <div className="text-center text-muted-foreground">
                        <QrCode className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Pilih shortlink untuk melihat QR</p>
                      </div>
                    </div>
                  )}
                  {isPreviewing && (
                    <Loader2 className="w-5 h-5 animate-spin absolute top-3 right-3 text-muted-foreground" />
                  )}
                </div>

                {previewQr && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={downloadQr}>
                      <Download className="w-4 h-4 mr-2" /> Download
                    </Button>
                    <Button variant="outline" onClick={copyQr}>
                      {copied ? (
                        <Check className="w-4 h-4 mr-2 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copied ? "Disalin!" : "Salin"}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Galeri semua QR tersimpan */}
        {links.length > 0 && (
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Semua QR Code Anda</CardTitle>
              <CardDescription>Klik salah satu untuk membuka kembali di editor.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {links.map((link) => (
                  <button
                    key={link.link_id}
                    type="button"
                    onClick={() => {
                      setSelectedLinkId(link.link_id);
                      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className={`rounded-lg border p-3 text-center transition-colors ${
                      link.link_id === selectedLinkId ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                    }`}
                  >
                    <div className="w-full aspect-square rounded-md bg-white flex items-center justify-center overflow-hidden mb-2">
                      {link.qr_code ? (
                        <img src={link.qr_code} alt="" className="w-full h-full object-contain p-1" />
                      ) : (
                        <QrCode className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    <p className="text-xs font-medium truncate">{link.title || `/${link.short_code}`}</p>
                    <p className="text-[11px] text-muted-foreground truncate">/{link.short_code}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default QRCodePage;
